import React from 'react';
import { BaseComponent } from '../../../components/BaseComponent';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose } from 'redux';
import styled from 'styled-components';
import { createStructuredSelector } from 'reselect';
import { Content } from '../../../components/Content';
import { FormField, FormFields } from '../../../components/FormFields';
import { Input } from '../../../components/Input';
import { Button } from '../../../components/Buttons';
import { Dropdown } from '../../../components/Dropdown';
import 'react-datepicker/dist/react-datepicker.css';
import DatePicker from 'react-datepicker';
import { calculateBmp } from '../actions';
import { HBox, VBox } from '../../../components/Box';
import { FieldError } from '../../../components/FieldError';
import { BpmResultContainer } from '../components/BpmResultContainer';
import { Checkbox } from '../../../components/Checkbox';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

const Container = styled.div``;

const STATE = {
  vehicleType: null,
  fuelType: null,
  regInternational: null,
  firstRegNetherlands: null,
  calculationDate: null,
  co2Emission: '',
  wltpCo2Emission: '',
  nedcCo2Emission: '',
  netPrice: '',
  mileage: 0,
  consumptionPrice: null,
  sellPrice: null,
  result: null,
  error: null,
  fieldErrors: null,
  exRentalDiscount: false,
};

/* eslint-disable react/prefer-stateless-function */
class BmpCalculatorComp extends BaseComponent {
  constructor(props) {
    super(props, STATE);
  }

  onVehicleTypeChange(evt) {
    const { value } = evt;
    this.setState((prev, props) => {
      return { ...prev, vehicleType: value };
    });
    return this;
  }

  onFuelTypeChange(evt) {
    const { value } = evt;
    this.setState((prev, props) => {
      return { ...prev, fuelType: value };
    });
    return this;
  }

  onInternationalRegChange(value) {
    this.setState((prev, props) => {
      return { ...prev, regInternational: value };
    });
    return this;
  }

  onCalculationRegChange(value) {
    this.setState((prev, props) => {
      return { ...prev, calculationDate: value };
    });
    return this;
  }

  onFirstRegChange(value) {
    this.setState((prev, props) => {
      return { ...prev, firstRegNetherlands: value, calculationDate: value };
    });
    return this;
  }

  onExRentalDiscountChange() {
    this.setState((prev, props) => {
      return { ...prev, exRentalDiscount: !prev.exRentalDiscount };
    });
    return this;
  }

  get firstRegDate() {
    return this.state.firstRegNetherlands;
  }

  get internationalRegDate() {
    return this.state.regInternational;
  }

  get calculationDate() {
    return this.state.calculationDate;
  }

  get wltpCo2Emission() {
    return this.state.wltpCo2Emission;
  }

  get nedcCo2Emission() {
    return this.state.nedcCo2Emission;
  }

  get netPrice() {
    return this.state.netPrice;
  }

  get mileage() {
    return this.state.mileage;
  }

  get amount() {
    return this.state.amount;
  }

  get exRentalDiscount() {
    return this.state.exRentalDiscount;
  }

  get error() {
    return this.state.error;
  }

  get vehicleTypeOptions() {
    return [
      {
        label: 'Passenger car',
        value: 'passengerCar',
      },
      {
        label: 'Delivery van',
        value: 'deliveryVan',
      },
      {
        label: 'Motorcycle',
        value: 'motorcycle',
      },
    ];
  }

  get fuelTypes() {
    return [
      {
        label: 'Petrol',
        value: 'petrol',
      },
      {
        label: 'LPG',
        value: 'lpg',
      },
      {
        label: 'Diesel',
        value: 'diesel',
      },
      {
        label: 'PHEV',
        value: 'phev',
      },
      {
        label: 'PHEV (with diesel engine)',
        value: 'phev_diesel',
      },
    ];
  }

  get vehicleTypeErrors() {
    return this.state.fieldErrors?.vehicleType;
  }

  get fuelTypeErrors() {
    return this.state.fieldErrors?.fuelType;
  }

  get regInternationalErrors() {
    return this.state.fieldErrors?.regInternational;
  }

  get firstRegNetherlandsErrors() {
    return this.state.fieldErrors?.firstRegNetherlands;
  }

  get calculationDateErrors() {
    return this.state.fieldErrors?.calculationDate;
  }

  get netPriceErrors() {
    return this.state.fieldErrors?.netPrice;
  }

  get wltpCo2EmissionErrors() {
    return this.state.fieldErrors?.wltpCo2Emission;
  }

  get nedcCo2EmissionErrors() {
    return this.state.fieldErrors?.nedcCo2Emission;
  }

  calculate() {
    const input = {
      vehicleType: this.state.vehicleType,
      fuelType: this.state.fuelType,
      regInternational: this.state.regInternational,
      firstRegNetherlands: this.state.firstRegNetherlands,
      calculationDate: this.state.calculationDate,
      wltpCo2Emission: this.state.wltpCo2Emission === '' ? null : this.state.wltpCo2Emission,
      nedcCo2Emission: this.state.nedcCo2Emission === '' ? null : this.state.nedcCo2Emission,
      netPrice:
        typeof this.state.netPrice == 'string' ? parseFloat(this.state.netPrice?.replace(',', '.')) : this.state.netPrice,
      mileage: this.state.mileage,
      exRentalDiscount: this.state.exRentalDiscount,
    };

    this.props.calculate(input, data => {
      if (data.validationErrors) {
        const errors = data.validationErrors.reduce((prev, curr) => {
          prev[curr.property] = Object.values(curr.constraints);
          return prev;
        }, {});
        this.setState(prev => {
          return { ...prev, fieldErrors: errors };
        });
      } else {
        this.setState(prev => {
          return { ...prev, result: data, fieldErrors: null };
        });
      }
    });
  }

  reset() {
    this.setState(STATE);
  }

  render() {
    const selectedVehicleType = this.vehicleTypeOptions.find(o => o.value === this.state?.vehicleType);
    const selectedFuelType = this.fuelTypes.find(o => o.value === this.state?.fuelType);

    return (
      <Container>
        <h3>{this.lbl('tools.bpm.title')}</h3>
        <Content>
          <FormFields>
            <HBox>
              <VBox>
                <p>{this.lbl('tools.bpm.vehicleTypeInputLabel')}</p>
                <FormField>
                  <Dropdown
                    options={this.vehicleTypeOptions}
                    onChange={this.onVehicleTypeChange.bind(this)}
                    value={selectedVehicleType}
                  />
                  <FieldError errors={this.vehicleTypeErrors} />
                </FormField>
              </VBox>
              <VBox>
                <p>{this.lbl('tools.bpm.fuelTypeInputLabel')}</p>
                <FormField>
                  <Dropdown options={this.fuelTypes} onChange={this.onFuelTypeChange.bind(this)} value={selectedFuelType} />
                  <FieldError errors={this.fuelTypeErrors} />
                </FormField>
              </VBox>
            </HBox>
            <HBox>
              <VBox>
                <p>{this.lbl('tools.bpm.wltpCo2EmissionInputLabel')}</p>
                <FormField>
                  <Input value={this.wltpCo2Emission} name="wltpCo2Emission" onChange={this.onInputChange.bind(this)} />
                  <FieldError errors={this.wltpCo2EmissionErrors} />
                </FormField>
              </VBox>
              <VBox>
                <p>{this.lbl('tools.bpm.nedcCo2EmissionInputLabel')}</p>
                <FormField>
                  <Input value={this.nedcCo2Emission} name="nedcCo2Emission" onChange={this.onInputChange.bind(this)} />
                  <FieldError errors={this.nedcCo2EmissionErrors} />
                </FormField>
              </VBox>
            </HBox>
            <HBox>
              <VBox>
                <p>{this.lbl('tools.bpm.priceInputLabel')}</p>
                <FormField>
                  <Input value={this.netPrice} name="netPrice" onChange={this.onInputChange.bind(this)} />
                  <FieldError errors={this.netPriceErrors} />
                </FormField>
              </VBox>
              <VBox>
                <p>{this.lbl('tools.bpm.milageInputLabel')}</p>
                <FormField>
                  <Input value={this.mileage} name="mileage" onChange={this.onInputChange.bind(this)} />
                  <FieldError errors={this.mileageErrors} />
                </FormField>
              </VBox>
            </HBox>
            <HBox>
              <VBox>
                <p>{this.lbl('tools.bpm.regDateInputLabel')}</p>
                <FormField>
                  <DatePicker
                    dateFormat="dd-MM-yyyy"
                    selected={this.internationalRegDate}
                    onChange={this.onInternationalRegChange.bind(this)}
                  />
                  <FieldError errors={this.regInternationalErrors} />
                </FormField>
              </VBox>
            </HBox>
            <HBox>
              <VBox>
                <p>{this.lbl('tools.bpm.firstRegNetherlandsDateInputLabel')}</p>
                <FormField>
                  <DatePicker
                    dateFormat="dd-MM-yyyy"
                    selected={this.firstRegDate}
                    onChange={this.onFirstRegChange.bind(this)}
                  />
                  <FieldError errors={this.firstRegNetherlandsErrors} />
                </FormField>
              </VBox>
              <VBox>
                <p>{this.lbl('tools.bpm.calculationDateInputLabel')}</p>
                <FormField>
                  <DatePicker
                    dateFormat="dd-MM-yyyy"
                    selected={this.calculationDate}
                    onChange={this.onCalculationRegChange.bind(this)}
                  />
                  <FieldError errors={this.calculationDateErrors} />
                </FormField>
              </VBox>
            </HBox>
            <HBox>
              <VBox>
                <Checkbox checked={this.exRentalDiscount} onChange={this.onExRentalDiscountChange.bind(this)}>
                  {this.lbl('tools.bpm.exRentalDiscountInputLabel')}
                </Checkbox>
              </VBox>
            </HBox>
          </FormFields>
        </Content>
        <HBox>
          <Button primary onClick={this.reset.bind(this)}>
            {this.lbl('tools.bpm.clearBtn')}
          </Button>
          <Button primary onClick={this.calculate.bind(this)}>
            {this.lbl('tools.bpm.calculateBtn')}
          </Button>
        </HBox>
        <BpmResultContainer result={this.state?.result} />
      </Container>
    );
  }
}

BmpCalculatorComp.propTypes = {
  dispatch: PropTypes.func.isRequired,
};

const mapStateToProps = (state, ownProps) =>
  createStructuredSelector({
    vehicleTypeErrors: state => state?.fieldErrors?.vehicleType ?? null,
  });

function mapDispatchToProps(dispatch, ownProps) {
  return {
    dispatch,
    fetch: () => {},
    calculate(input, cb) {
      dispatch(calculateBmp(input, cb));
    },
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export const BmpCalculator = compose(withConnect, withRouter)(BmpCalculatorComp);
